import { Button, Typography, Stack, Divider, Alert, CircularProgress } from "@mui/material";
import { useMemo, useState, useEffect } from "react";
import DownloadForOfflineOutlinedIcon from "@mui/icons-material/DownloadForOfflineOutlined";
import { fileService } from "../../Core/Api/services";
import { Connect, ConnectInstaller } from "@ibm-aspera/connect-sdk-js";
import { useParams } from "react-router";
import { FileInfosResponse } from "../../Core/Api/Types/file-infos-response";
import PendingIcon from "../../Components/Misc/PendingIcon";
import { FileTransferInitiator } from "../../Core/Api/Types/file-transfer-initiator";
import moment from "moment";
import { FileTransferStatus } from "../../Core/Api/Types/file-transfer-status";
import { TransferSpecDTO } from "../../Core/Api/Types/aspera-transfer-spec-basic";
import LogLayout from "../../Layouts/Log";
import DataTable, { CellSize } from "../../Components/Table/table";
import { GridActionsCellItem, GridColDef, GridRowParams } from "@mui/x-data-grid";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import { GetFileSize } from "../../Utils/helpers";
import { ConnectClientType } from "@ibm-aspera/connect-sdk-js/dist/esm/core/types";
import { useTranslation } from "react-i18next";

function DownloadLink() {
    const { t } = useTranslation();
    const key: string = useParams().key!;
    const enqueueSnackbar = useCustomSnackbar();

    const [isLoading, setIsLoading] = useState(true);
    const [downloadInfo, setDownloadInfo] = useState<FileInfosResponse>(new FileInfosResponse());
    const [connect, setConnect] = useState<ConnectClientType>(new Connect({}));
    const [creationDate, setCreationDate] = useState<string>("no date loaded");
    const [expirationDate, setExpirationDate] = useState<string>("no date loaded");
    const [isExpired, setExpiration] = useState<boolean>(true);

    const isHttpDownloadable = useMemo(
        () =>
            !downloadInfo.infos?.files?.some((x) => x.status !== FileTransferStatus.Online) &&
            downloadInfo.infos?.initiator === FileTransferInitiator.Http &&
            !isExpired,
        [downloadInfo.infos?.files, downloadInfo.infos?.initiator, isExpired]
    );

    //#region Aspera Connect
    // useMemo for Aspera Connect and ConnectInstaller avoid connectToReinitialize on component re-rendering
    const asperaConnectInstaller = useMemo(
        () => (!isHttpDownloadable ? new ConnectInstaller() : undefined),
        [isHttpDownloadable]
    );

    // StatusListener event function, manages install, update of aspera on OS and connect status of AsperaConnect
    const connectStatusEventListener = (eventType: string, data: string) => {
        if (!asperaConnectInstaller) return;

        if (eventType === Connect.EVENT.STATUS && data === Connect.STATUS.INITIALIZING) {
            asperaConnectInstaller.showLaunching();
        } else if (eventType === Connect.EVENT.STATUS && data === Connect.STATUS.EXTENSION_INSTALL) {
            asperaConnectInstaller.showExtensionInstall();
        } else if (eventType === Connect.EVENT.STATUS && data === Connect.STATUS.FAILED) {
            asperaConnectInstaller.showDownload();
        } else if (eventType === Connect.EVENT.STATUS && data === Connect.STATUS.OUTDATED) {
            asperaConnectInstaller.showUpdate();
        } else if (eventType === Connect.EVENT.STATUS && data === Connect.STATUS.RUNNING) {
            asperaConnectInstaller.connected(1000);
        }
    };

    // Function that init Aspera connect driver
    const initAsperaConnect = () => {
        let localConnect: ConnectClientType = new Connect({});
        // we add the connect listener to connect on status
        localConnect.addEventListener(Connect.EVENT.STATUS, connectStatusEventListener);
        localConnect.initSession();
        setConnect(localConnect);

        console.log("Aspera connect initialized");
    };
    //#endregion

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: "name",
                headerName: t("table:headerName.fileName"),
                flex: CellSize.xlarge,
                minWidth: CellSize.xlarge,
                disableColumnMenu: true,
                renderCell: (data: any) => (
                    <Typography variant="body2" pt={1} pb={1} sx={{ wordBreak: "break-all" }}>
                        {data.value}
                    </Typography>
                ),
            },
            {
                field: "size",
                headerName: t("common:size"),
                flex: CellSize.medium,
                minWidth: CellSize.medium,
                disableColumnMenu: true,
                valueGetter: (value: any) => GetFileSize(parseInt(value)),
            },
            {
                field: "actions",
                headerName: "",
                type: "actions",
                disableColumnMenu: true,
                getActions: (params: GridRowParams) => [
                    <GridActionsCellItem
                        icon={
                            <a href={fileService.DownloadHttpFileWithFileName(key, params?.row?.name!)} download>
                                <DownloadForOfflineOutlinedIcon sx={{ width: 20, height: 20, color: "text.primary" }} />
                            </a>
                        }
                        label={t("common:download")}
                    />,
                ],
            },
        ],
        [key, fileService, isHttpDownloadable]
    );

    const onclickAsperaDownload = () => {
        fileService.DownloadAsperaFile(key).then((data) => {
            if (!data.success || data.transferSpec === null) {
                enqueueSnackbar(t("alert:error.oopsWithMessage", { messgae: data.message }), "warning");
                return;
            }

            try {
                let result = connect.startTransfer(
                    TransferSpecDTO(data.transferSpec),
                    { allow_dialogs: false },
                    { error: (err) => console.log(err) }
                );

                if (result.request_id !== undefined) enqueueSnackbar(t("alert:success.transferBeginInFewSeconds"), "success");
                else enqueueSnackbar(t("alert:error.pluginHasRejectedTransfer"), "error");
            } catch {
                initAsperaConnect();

                enqueueSnackbar(t("alert:error.pluginHasRejectedTransfer"), "error");
            }
        });
    };

    const downloadButton = useMemo(() => {
        if (downloadInfo.infos?.initiator === FileTransferInitiator.Http)
            return (
                <a href={fileService.DownloadHttpFile(key)} download>
                    <Button
                        endIcon={<DownloadForOfflineOutlinedIcon />}
                        color="primary"
                        variant="contained"
                        disabled={(downloadInfo.infos?.files?.length ?? 0) <= 0 || isExpired}
                        sx={{ width: "100%", mt: 4 }}
                    >
                        {t("common:download")}{" "}
                        {GetFileSize(downloadInfo.infos?.files?.flatMap((x) => x.size).reduce((a, c) => a! + c!, 0) ?? 0)}
                    </Button>
                </a>
            );
        else
            return (
                <Button
                    endIcon={<DownloadForOfflineOutlinedIcon />}
                    color="primary"
                    variant="contained"
                    onClick={onclickAsperaDownload}
                    disabled={(downloadInfo.infos?.files?.length ?? 0) <= 0 || isExpired}
                    sx={{ width: "100%", mt: 4 }}
                >
                    {t("button:downloadWithAsperaConnect", {
                        size: GetFileSize(downloadInfo.infos?.files?.flatMap((x) => x.size).reduce((a, c) => a! + c!, 0) ?? 0),
                    })}
                </Button>
            );
    }, [key, isHttpDownloadable, downloadInfo.infos?.files?.length]);

    useEffect(() => {
        fileService.GetFileInfos(key).then((data) => {
            setDownloadInfo(data);
            setIsLoading(false);

            if (!data.infos) return;

            setCreationDate(moment(data.infos.createdAt)?.fromNow());

            if (data.infos.initiator === FileTransferInitiator.AsperaConnectFasp) initAsperaConnect();

            let hasExpired: boolean = !moment(data.infos.expireDate).isAfter(Date.now());

            setExpiration(hasExpired);
            setExpirationDate((hasExpired ? "Has expired " : "Will expire ") + moment(data.infos.expireDate)?.fromNow());
        });
    }, [key]);

    return (
        <LogLayout platformName={downloadInfo?.brandName!} catchSentence="Download center.">
            <Stack height={"100%"}>
                <Stack>
                    <PendingIcon
                        sx={{ mr: "auto", ml: "auto", top: 20 }}
                        color={isLoading ? "blue" : downloadInfo.infos?.status === "Online" && !isExpired ? "blue" : "red"}
                        icon={<DownloadForOfflineOutlinedIcon />}
                    />

                    {!isLoading && downloadInfo.success && (
                        <>
                            <Typography mt={4} mb={1} component="h1" variant="h3" textAlign="center">
                                {downloadInfo.brandName}
                            </Typography>
                            <Typography variant="subtitle2" color="text.tier" fontWeight={400} textAlign="center">
                                {t("common:createdBy")} {downloadInfo.infos?.createdByUserName} {creationDate}
                            </Typography>
                            <Typography variant="subtitle2" color="text.tier" fontWeight={400} textAlign="center">
                                {expirationDate}
                            </Typography>
                            {downloadInfo.orderItemSummarize && (
                                <Typography variant="body2" color="text.tier" textAlign="center" mt={2}>
                                    {downloadInfo.orderItemSummarize}
                                </Typography>
                            )}
                            {!isExpired ? downloadButton : <Alert severity="error">{t("alert:error.transferHasExpired")}</Alert>}
                        </>
                    )}

                    {!isLoading && !downloadInfo.success && (
                        <Alert severity="error" sx={{ mt: 4, mb: 1 }}>
                            {t("alert:error.oopsWithMessage", { message: downloadInfo.message })}
                        </Alert>
                    )}

                    {isLoading && <CircularProgress sx={{ position: "relative", top: "50%", left: "46%" }} />}
                </Stack>

                {(downloadInfo.infos?.files?.length ?? 0) > 0 && (
                    <>
                        <Divider sx={{ mt: 2, mb: 2 }} />
                        <Stack height="100%" overflow={"scroll"}>
                            <DataTable
                                noControls
                                rows={
                                    downloadInfo.infos?.files?.map((x, index) => {
                                        return { id: index, ...x };
                                    }) || []
                                }
                                columnHeaderHeight={48}
                                rowHeight={56}
                                getRowHeight={() => "auto"}
                                pagination={false}
                                disableRowSelectionOnClick={true}
                                onRowClick={(x: any) => {
                                    navigator.clipboard.writeText(x.row.name!);
                                    enqueueSnackbar(t("alert:primary.fileNameCopiedToClipboard"), "primary");
                                }}
                                columnVisibilityModel={{ actions: isHttpDownloadable }}
                                columns={columns}
                            />
                        </Stack>
                    </>
                )}
            </Stack>
        </LogLayout>
    );
}

export default DownloadLink;
